import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';

import uuidv4 from './uuidv4';
import { User } from '../services/user';
import { STORAGE_KEY } from '../auth/context/jwt/auth-provider';

export const dateFormat = (value?: string, formatStr?: string) =>
  // eslint-disable-next-line no-nested-ternary
  value ? dayjs(value).format(formatStr || 'YYYY-MM-DD') : '-';
export const getUserFullName = (user?: User, noneText?: string) =>
  user ? `${user.lastName}${user.firstName}` : noneText || '-';
export function isEmpty(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'object' && Object.keys(value).length === 0) ||
    value === ''
  );
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isObjectEmpty(obj: Record<string, any>): boolean {
  // eslint-disable-next-line no-restricted-syntax
  for (const key in obj) {
    if (!isEmpty(obj[key])) {
      return false;
    }
  }
  return true;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function filterEmptyValues(obj: { [key: string]: any }) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v !== null && v !== undefined && v !== '')
  );
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const paramsQsStringify = (obj?: { [key: string]: any }) => {
  if (!obj) return '';
  const newObj = filterEmptyValues(obj);
  return isObjectEmpty(newObj) ? '' : `?${queryString.stringify(newObj)}`;
};

export const replaceSymbols = (input: string) => input.replace(/[+#%]/g, '-');

/**
 * 将图片的 URL 转换为文件
 * @param imageUrl 图片的 URL
 * @param fileName 文件名（默认值为 `edited-image.jpg`）
 * @param fileType 文件类型（默认值为 `image/jpeg`）
 * @returns 返回生成的文件对象
 */
export const fetchImageAsFile = async (
  imageUrl: string,
  fileName: string = 'edited-image.jpg',
  fileType: string = 'image/jpeg'
): Promise<File> => {
  const response = await fetch(imageUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.statusText}`);
  }

  const blob = await response.blob();
  return new File([blob], fileName, { type: fileType });
};

export * from './date';
// 新增工具函数导出
export * from './subjectUtils';

// 本地时间转ISO字符串（不带Z）
export function toLocalISOString(date: Date) {
  const pad = (n: number) => n.toString().padStart(2, '0');
  const padMs = (n: number) => n.toString().padStart(3, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${padMs(date.getMilliseconds())}`;
}
const getTableScrollY = (
  list?: unknown[],
  height?: string | unknown
): string | number | undefined =>
  list?.length ? ((height || 'calc(100vh - 200px)') as string | number) : undefined;
export const getScrollConfig = (list?: unknown[]) => ({
  x: 'max-content',
  y: getTableScrollY(list),
});
export function UseGetReFetchEnable(path: string) {
  const location = useLocation();
  return location.pathname === path;
}

export const toLogin = () => {
  localStorage.setItem(STORAGE_KEY, '');
  window.location.href = `${import.meta.env.VITE_USER_URL}/login?${new URLSearchParams({
    returnTo: window.location.pathname + window.location.search,
  }).toString()}`;
  // window.location.href = `http://localhost:5173/login?${new URLSearchParams({
  //   returnTo: window.location.pathname + window.location.search,
  // }).toString()}`;
};
export function parseDecimal(value: unknown): number {
  if (value == null) return 0;
  let raw = String(value).trim();

  raw = raw
    .replace(/[,，\s]/g, '')
    .replace(/[¥$€£￥]/g, '')
    .replace(/%/g, '');

  try {
    return new Decimal(raw).toNumber();
  } catch {
    return 0;
  }
}

/**
 * 获取设备ID
 * @returns deviceId
 */
export const getDeviceId = () => {
  // 生成唯一 deviceId（示例：随机数+时间戳）
  const deviceId = getCrossDomainDeviceId() || localStorage.getItem('deviceId') || uuidv4();
  // 写入 Cookie，domain 为父域名，path 为根路径，过期时间1年
  document.cookie = `deviceId=${deviceId}; domain=.dnjapex.com; path=/; max-age=31536000; SameSite=None; Secure`;
  // 同时存 localStorage 做兜底
  localStorage.setItem('deviceId', deviceId);

  return deviceId;
};

// 读取 Cookie（任意子域名下执行）
function getCrossDomainDeviceId() {
  const cookieArr = document.cookie.split('; ');
  const deviceId =
    cookieArr.map((cookie) => cookie.split('=')).find(([key]) => key === 'deviceId')?.[1] || '';
  return deviceId;
}
